import Dialog from '@mui/material/Dialog';
import { FC, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useTranslation } from 'react-i18next';
import { ToastSeverity, useToast } from 'thermia-react-container';
import { RegistrationsContext } from '../../../../context/contextProvider';
import {ApiErrorHelper, RegistrationConflictError} from '../../../../validation/Errors';
import CompleteError from './completeError';
import CompleteForm from './completeForm';
import CompleteSuccess from './completeSuccess';
import {useRefreshData} from "../../hooks/useRefreshData";

type PageStatus = 'init' | 'success' | 'error';

interface Props {
  open: boolean;
  onClose: (value?: string) => void;
}

export type DisplayError =
  | 'deviceError'
  | 'conflict'
  | 'other'
  | 'registrationIsAlreadyCompleted'
  | 'errorRegistrationUnableToCompleteDueItHasBeenDeleted';

const CompleteRegistrationDialog: FC<Props> = ({ open, onClose }) => {
  const [pageStatus, setPageStatus] = useState<PageStatus>('init');
  const [error, setError] = useState<DisplayError>();
  const { registrations } = useContext(RegistrationsContext);
  const { completeRegistration } = registrations;
  const refreshData = useRefreshData();
  const { addToast } = useToast();
  const { t } = useTranslation('registrations');

  const navigate = useNavigate();

  useEffect(() => {
    if (open) {
      setPageStatus('init');
    }
  }, [open]);

  const [loading, setLoading] = useState(false);

  const handleClick = async () => {
    try {
      setLoading(true);
      await completeRegistration();
      refreshData();
      setPageStatus('success');
    } catch (error) {
      if (error instanceof RegistrationConflictError && error.isConflict) {
        if(await error.isRegistrationConflict()) {
          setError('conflict');
        }
        else if(error.isRegistrationCompleted) {
          setError('registrationIsAlreadyCompleted');
        }
        else if(await error.isDeviceConflict()) {
          setError('deviceError');
        }
      }

      if (ApiErrorHelper.isOnsiteVersionInvalid(error)) {
        setLoading(false);
        return addToast(t('registrations.general.errorOnsiteVersionIsInvalid'), ToastSeverity.ERROR);
      }

      if (ApiErrorHelper.isNotFound(error)) {
        setError('errorRegistrationUnableToCompleteDueItHasBeenDeleted');
      }
     
      setPageStatus('error');
    }
    setLoading(false);
  };

  const handleCancel = () => {
    if (error === 'registrationIsAlreadyCompleted' || error === 'errorRegistrationUnableToCompleteDueItHasBeenDeleted') {
      navigate('/registrations');
    }
    onClose();
  };

  return (
    <Dialog id="complete-registration-dialog" sx={{ '& .MuiDialog-paper': { width: '80%' } }} maxWidth="xs" open={open}>
      {pageStatus === 'init' && <CompleteForm loading={loading} handleClick={handleClick} handleCancel={handleCancel} />}
      {pageStatus === 'success' && <CompleteSuccess handleCancel={handleCancel} />}
      {pageStatus === 'error' && <CompleteError onClick={handleCancel} error={error} />}
    </Dialog>
  );
};

export default CompleteRegistrationDialog;
